/**
 * Badges for surfacing customer-level deposit-related context on draft
 * invoice rows. Two distinct signals — finance treats them differently:
 *
 *   - **Unapplied credit**: a credit memo already exists on the customer
 *     (typically because a deposit invoice closed-paid and our webhook
 *     created the credit). Can be applied to other invoices right now.
 *
 *   - **Deposit pending**: a deposit invoice has been issued to the
 *     customer but hasn't been paid yet. The credit memo doesn't exist
 *     yet — it materializes when the deposit closes-paid via webhook.
 *     Useful warning that "money is incoming, don't bill them more for
 *     the same period without accounting for it."
 *
 * Renders nothing when neither value is > 0 so callers can use it
 * unconditionally.
 */
export function CreditAvailableBadge({
  unappliedCredit,
  pendingDeposit,
  /** Legacy single-amount prop — kept for the stale-drafts table that
   *  hasn't been refactored to pass both values. Treated as unappliedCredit. */
  amount,
}: {
  unappliedCredit?: number | null;
  pendingDeposit?: number | null;
  amount?: number | null;
}) {
  const credit = unappliedCredit ?? amount ?? 0;
  const pending = pendingDeposit ?? 0;
  const hasCredit  = credit > 0.01;
  const hasPending = pending > 0.01;
  if (!hasCredit && !hasPending) return null;

  const fmt = (n: number) => n.toLocaleString(undefined, {
    style: 'currency', currency: 'USD', minimumFractionDigits: 2,
  });

  return (
    <>
      {hasCredit && (
        <span
          title={`Customer has ${fmt(credit)} in unapplied credit. Apply it in ChargeOver before approving / sending so the recipient sees the correct remaining balance.`}
          style={badgeStyle('amber')}
        >
          💰 {fmt(credit)} credit
        </span>
      )}
      {hasPending && (
        <span
          title={`Customer has ${fmt(pending)} of issued-but-unpaid deposit invoices. The credit memo doesn't exist yet — it's created when the deposit closes-paid. Don't approve / email this draft until the deposit lands and the credit is applied (or apply it manually after the fact).`}
          style={badgeStyle('blue')}
        >
          🕒 {fmt(pending)} deposit pending
        </span>
      )}
    </>
  );
}

function badgeStyle(variant: 'amber' | 'blue'): React.CSSProperties {
  const bg = variant === 'amber' ? 'var(--color-amber-bg)' : 'var(--color-blue-bg)';
  const fg = variant === 'amber' ? 'var(--color-amber-text)' : 'var(--color-blue-text)';
  const border = variant === 'amber' ? 'var(--color-amber)' : 'var(--color-blue)';
  return {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    fontSize: 10, fontWeight: 500, padding: '2px 6px',
    borderRadius: 'var(--radius-sm)',
    background: bg, color: fg, border: `0.5px solid ${border}`,
    marginLeft: 6, whiteSpace: 'nowrap',
  };
}
